import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/icons";
import { Banner, Button, EmptyState, StatusBadge } from "../components/ui";
import { useSession } from "../context/Session";
import { listVisits } from "../lib/api";
import { supabase } from "../lib/supabase";

const ranges = [
  ["today", "Today"],
  ["7", "7 days"],
  ["30", "30 days"],
];

function withinRange(value, range) {
  if (!value) return false;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (range === "today") return time >= start.getTime();
  start.setDate(start.getDate() - Number(range));
  return time >= start.getTime();
}

function StatCard({ icon, label, to, tone, value, detail }) {
  return (
    <Link to={to} className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tone}`}>
        <Icon name={icon} className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <span className="mt-0.5 block text-2xl font-semibold leading-none text-brand-ink">{value}</span>
        <span className="mt-1 block text-xs text-slate-500">{detail}</span>
      </span>
      <Icon name="arrow" className="h-4 w-4 text-slate-300 transition duration-200 group-hover:text-slate-500" />
    </Link>
  );
}

function Panel({ title, subtitle, action, children }) {
  return (
    <section className="flex min-h-[280px] flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        </div>
        {action}
      </div>
      <div className="mt-4 flex flex-1 flex-col">{children}</div>
    </section>
  );
}

function KioskRing({ online, offline, disabled }) {
  const total = online + offline + disabled;
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const parts = [
    ["Online", online, "#16a34a"],
    ["Offline", offline, "#e11d48"],
    ["Disabled", disabled, "#94a3b8"],
  ];
  let consumed = 0;
  const segments = parts.map(([name, count, color]) => {
    const length = total > 0 ? (count / total) * circumference : 0;
    const segment = { name, count, color, length, offset: consumed };
    consumed += length;
    return segment;
  });
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 sm:flex-row">
      <div className="relative h-36 w-36">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90" role="img" aria-label={`${total} kiosks. ${online} online, ${offline} offline, ${disabled} disabled.`}>
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="12" />
          {segments.filter((segment) => segment.length > 0).map((segment) => (
            <circle key={segment.name} cx="60" cy="60" r={radius} fill="none" stroke={segment.color} strokeWidth="12" strokeDasharray={`${segment.length} ${circumference - segment.length}`} strokeDashoffset={-segment.offset} />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold">{total}</span>
          <span className="text-[11px] text-slate-500">Total kiosks</span>
        </div>
      </div>
      <ul className="space-y-2 text-sm">
        {parts.map(([name, count, color]) => (
          <li key={name} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
            <span className="w-16 text-slate-600">{name}</span>
            <span className="font-semibold">{count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Dashboard() {
  const { orgId, can } = useSession();
  const [visits, setVisits] = useState(null);
  const [sites, setSites] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [approvals, setApprovals] = useState(0);
  const [error, setError] = useState("");
  const [range, setRange] = useState("today");

  useEffect(() => {
    if (!orgId) return undefined;
    let active = true;
    async function load() {
      const [visitResult, approvalResult, siteResult, kioskResult] = await Promise.all([
        listVisits(orgId),
        supabase.from("approvals").select("id", { count: "exact", head: true }).eq("organization_id", orgId).eq("status", "pending"),
        supabase.from("sites").select("id, status").eq("organization_id", orgId),
        supabase.from("kiosks").select("id, status").eq("organization_id", orgId),
      ]);
      if (!active) return;
      if (visitResult.error) setError(visitResult.error.message);
      else setError("");
      setVisits(visitResult.data || []);
      setApprovals(approvalResult.count || 0);
      setSites(siteResult.data || []);
      setKiosks(kioskResult.data || []);
    }
    load();
    const channel = supabase.channel(`visits-${orgId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "visits", filter: `organization_id=eq.${orgId}` }, load)
      .subscribe();
    return () => { active = false; supabase.removeChannel(channel); };
  }, [orgId]);

  const ready = visits !== null;
  const onSite = (visits || []).filter((visit) => visit.status === "checked_in");
  const expected = (visits || []).filter((visit) => ["expected", "invited", "approved"].includes(visit.status));
  const checkedInToday = (visits || []).filter((visit) => withinRange(visit.checked_in_at, "today"));
  const filtered = useMemo(
    () => (visits || []).filter((visit) => withinRange(visit.checked_in_at || visit.expected_arrival, range)),
    [visits, range],
  );
  const recent = filtered.slice(0, 5);
  const topSites = useMemo(() => {
    const counts = new Map();
    filtered.forEach((visit) => {
      const key = visit.sites?.name || "Site";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [filtered]);
  const maxCount = topSites[0]?.[1] || 1;
  const kioskOnline = kiosks.filter((kiosk) => kiosk.status === "online").length;
  const kioskOffline = kiosks.filter((kiosk) => kiosk.status === "offline").length;
  const kioskDisabled = kiosks.filter((kiosk) => kiosk.status === "disabled").length;
  const cards = [
    ["visits", "On site", "/visitors?view=onsite", "bg-amber-50 text-amber-600", onSite.length, `${expected.length} approved or invited`],
    ["calendar", "Expected", "/visitors?view=expected", "bg-sky-50 text-brand-blue", expected.length, `${checkedInToday.length} checked in today`],
    ["sites", "Sites", "/sites", "bg-emerald-50 text-emerald-600", sites.length, `${sites.filter((site) => site.status === "active").length} active`],
    ["kiosks", "Kiosks", "/kiosks", "bg-violet-50 text-violet-600", kiosks.length, `${kioskOnline} online · ${kioskOffline} offline · ${kioskDisabled} disabled`],
    ["audit", "Approvals", "/approvals", "bg-rose-50 text-rose-600", approvals, "Waiting for a decision"],
    ["staff", "Checked in today", "/visitors", "bg-sky-50 text-sky-600", checkedInToday.length, "Arrivals recorded today"],
  ];

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Who is on site, who is expected, and what still needs a decision. Totals are current. The date filter applies to the panels below.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="relative">
            <span className="sr-only">Date range for activity panels</span>
            <Icon name="calendar" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <select className="h-10 rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-8 text-sm font-semibold" value={range} onChange={(event) => setRange(event.target.value)}>
              {ranges.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          {can("visitors.create") ? (
            <Link to="/visitors/new" className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-brand-orange px-4 text-sm font-semibold text-white hover:bg-[#c4631d]">
              <Icon name="plus" className="h-4 w-4" /> Invite visitor
            </Link>
          ) : null}
        </div>
      </div>
      {error ? <div className="mb-4"><Banner>{error}</Banner></div> : null}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {ready ? cards.map(([icon, label, to, tone, value, detail]) => (
          <StatCard key={label} icon={icon} label={label} to={to} tone={tone} value={value} detail={detail} />
        )) : Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="h-[88px] animate-pulse rounded-xl border border-slate-200 bg-white" />
        ))}
      </div>
      <div className="mt-4 grid gap-3 xl:grid-cols-3">
        <Panel title="Recent visits" subtitle="Latest visitor activity" action={<Link to="/visitors" className="text-xs font-semibold text-brand-blue">View all</Link>}>
          {!ready ? <div className="h-40 animate-pulse rounded-lg bg-slate-50" /> : recent.length === 0 ? <EmptyState compact icon="visits" title="No visits in this period" body="Invite a visitor to start the lifecycle." action={can("visitors.create") ? <Link to="/visitors/new"><Button>Invite visitor</Button></Link> : null} /> : (
            <ul className="divide-y divide-slate-100">
              {recent.map((visit) => (
                <li key={visit.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span className="min-w-0">
                    <Link className="block truncate font-semibold text-brand-ink hover:text-brand-blue" to={`/visitors/${visit.id}`}>{visit.visitors?.first_name} {visit.visitors?.last_name}</Link>
                    <span className="block truncate text-xs text-slate-500">{visit.sites?.name || "Site"} · {visit.purpose}</span>
                  </span>
                  <StatusBadge status={visit.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Kiosk status" subtitle="Current status of kiosks at this organization" action={<Link to="/kiosks" className="text-xs font-semibold text-brand-blue">View all</Link>}>
          {!ready ? <div className="h-40 animate-pulse rounded-lg bg-slate-50" /> : <KioskRing online={kioskOnline} offline={kioskOffline} disabled={kioskDisabled} />}
        </Panel>
        <Panel title="Top sites by visits" subtitle="Most visited sites for the selected period" action={<Link to="/sites" className="text-xs font-semibold text-brand-blue">View all</Link>}>
          {!ready ? <div className="h-40 animate-pulse rounded-lg bg-slate-50" /> : topSites.length === 0 ? <EmptyState compact icon="sites" title="No site activity yet" body="Visit counts will appear here once visits start." action={<Link to="/sites"><Button variant="secondary">View sites</Button></Link>} /> : (
            <ul className="space-y-3">
              {topSites.map(([name, count]) => (
                <li key={name}>
                  <div className="mb-1 flex justify-between gap-3 text-sm">
                    <span className="truncate font-semibold">{name}</span>
                    <span className="text-slate-500">{count}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full bg-brand-blue" style={{ width: `${(count / maxCount) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
